import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import Box from '@mui/material/Box'
import Typography from '@mui/material/Typography'
import dayjs from 'dayjs'
import Dialog from '@mui/material/Dialog'
import DialogContent from '@mui/material/DialogContent'
import Divider from '@mui/material/Divider'
import IconButton from '@mui/material/IconButton'
import Tooltip from '@mui/material/Tooltip'
import EditRoundedIcon from '@mui/icons-material/EditRounded'
import { useAtomValue } from 'jotai'
import Table from '@mui/material/Table'
import TableBody from '@mui/material/TableBody'
import TableCell from '@mui/material/TableCell'
import TableHead from '@mui/material/TableHead'
import TableRow from '@mui/material/TableRow'
import AddRoundedIcon from '@mui/icons-material/AddRounded'
import Button from '@mui/material/Button'

import ErrorBox from "@/components/error-box"
import LoadingBox from '@/components/loading-box'
import { useYearlyFeePayer } from '@/hooks/api/use-yearly-fee-payers'
import RemoveDialog from '../remove-dialog'
import { DISCOUNT_TYPE_LABEL_MAP } from '@/constants/general'
import { convertFromCents, printHumanDateTime, printPrice, roundPrice } from '@/helpers'
import YearlyFeeDiscountActions from '../actions'
import FeedbackAlerts from '@/components/feedback-alerts'
import YearlyFeePayerForm from '@/components/forms/yearly-fee-payer'
import type { YearlyFeePayerSingle } from '@/types'
import YearlyFeeDiscountForm from '@/components/forms/yearly-fee-discount'
import YearlyFeePaymentForm from '@/components/forms/yearly-fee-payment'
import YearlyFeePaymentActions from './actions'

function YearlyFeePayerFeePayments({
    yearlyFeePayer,
}: {
    yearlyFeePayer: YearlyFeePayerSingle,
}) {

    const [success, setSuccess] = useState<string | null>(null)
    const [error, setError] = useState<string | null>(null)
    const [create, setCreate] = useState<boolean>(false)
    const [add, setAdd] = useState<boolean>(false)
    const [remove, setRemove] = useState<number | null>(null)

    return (
        <Box sx={{
            display: 'flex',
            flexDirection: 'column',
            gap: 1.5,
            overflowX: 'auto',
            mt: 3,
        }}>
            <Box px={2.5}>
                <Typography variant="h3" component="h2">
                    Platby
                </Typography>
            </Box>
            <FeedbackAlerts
                success={success}
                error={error}
                onClearSuccess={() => setSuccess(null)}
                onClearError={() => setError(null)}
            />
            {yearlyFeePayer.yearly_fee_payments?.length === 0 ?
                <Box px={2.5}>
                    Poplatník nemá nastavené žádné platby
                </Box>
            :
                <Table
                    size="small"
                    aria-label="Seznam plateb"
                    sx={{
                        mt: 1,
                        minWidth: 600,
                    }}
                >
                    <TableHead>
                        <TableRow>
                            <TableCell sx={{ paddingLeft: 3 }}>ID</TableCell>
                            <TableCell>Požadovaná částka (Kč)</TableCell>
                            <TableCell>Splatnost</TableCell>
                            <TableCell>Uhrazeno (Kč)</TableCell>
                            <TableCell>Datum úhrady</TableCell>
                            <TableCell align="right"/>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {yearlyFeePayer.yearly_fee_payments.map(row => (
                            <TableRow
                                key={row.id}
                                sx={{
                                    '&:last-child td, &:last-child th': { border: 0 },
                                }}
                            >
                                <TableCell component="th" scope="row" sx={{ paddingLeft: 3 }}>
                                    {row.id}
                                </TableCell>
                                <TableCell>
                                    {printPrice(convertFromCents(row.requested_amount_cents))}
                                </TableCell>
                                <TableCell>
                                    {dayjs(row.due_date).format('D. M. YYYY')}
                                </TableCell>
                                <TableCell>
                                    {row.paid_amount_cents ? printPrice(convertFromCents(row.paid_amount_cents)) : '-'}
                                </TableCell>
                                <TableCell>
                                    {row.paid_at ? dayjs(row.paid_at).format('D. M. YYYY') : '-'}
                                </TableCell>
                                <TableCell align="right">
                                    <YearlyFeePaymentActions
                                        yearlyFeePayment={row}
                                    />
                                </TableCell>
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            }
            <Box px={2.5} py={1.5} sx={{
                display: 'flex',
                flexWrap: 'wrap',
                gap: 2,
            }}>
                {add ?
                    <Box maxWidth="sm">
                        <YearlyFeePaymentForm
                            feePayerId={yearlyFeePayer.id}
                            closeCallback={() => setAdd(false)}
                        />
                    </Box>
                    :
                    <Box>
                        <Button
                            variant="contained"
                            color="terciary"
                            startIcon={<AddRoundedIcon />}
                            onClick={() => setAdd(true)}
                        >
                            Přidat poplatníkovi platbu
                        </Button>
                    </Box>
                }
            </Box>
            <Dialog
                key={'remove-'+remove}
                open={!!remove}
                onClose={() => setRemove(null)}
            >
                <DialogContent>
                    <RemoveDialog
                        yearlyFeeDiscountId={remove}
                        handleClose={() => setRemove(null)}
                    />
                </DialogContent>
            </Dialog>
        </Box>
    )
}

export default YearlyFeePayerFeePayments
